import React, { useState, useEffect } from 'react';
import { InventoryBalanceItem } from '../types';
import { api } from '../api';
import { tg } from '../telegram';
import { CameraCapture } from '../components/CameraCapture';
import { ConfirmView, ConfirmItem } from '../components/ui/ConfirmView';
import { SuccessView } from '../components/ui/SuccessView';
import { ItemDropdown } from '../components/ui/ItemDropdown';
import { AddTransferItemModal } from '../components/ui/AddTransferItemModal';
import { useSubmitAction } from '../hooks/useSubmitAction';
import { useMasterData } from '../hooks/useMasterData';
import { formatQty } from '../utils/format';
import { toComparisonKey } from '../utils/textNormalizer';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Plus,
  Trash2,
  RefreshCw,
  Camera,
  Upload,
  ArrowRight,
  ClipboardCheck,
} from 'lucide-react';

interface Props {
  onBack: () => void;
  onSuccess: () => void;
}

interface TransferItemRow {
  ingredient_name: string;
  quantity: number;
  unit: string;
  central_standard_qty: number;
  standard_unit: string;
  ratio: number;
  is_converted?: boolean;
  max_available: number;
}

export const StockTransferScreen: React.FC<Props> = ({ onBack, onSuccess }) => {
  const { ingredients, conversions, getInwardUnitsForItem, units, transferItems: transferOptions } = useMasterData();
  const [step, setStep] = useState<'INPUT' | 'CONFIRM' | 'SUCCESS'>('INPUT');
  const [balances, setBalances] = useState<InventoryBalanceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [transferItems, setTransferItems] = useState<TransferItemRow[]>([]);
  const [note, setNote] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.getInventory();
        setBalances(data);
        setTransferItems([
          { ingredient_name: '', quantity: 0, unit: '', central_standard_qty: 0, standard_unit: '', ratio: 1, is_converted: true, max_available: 0 },
        ]);
      } catch (err: any) {
        toast.error(`Không tải được kho: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Kho Tổng tuyệt đối không có BTP (Bán thành phẩm)
  const centralItems = balances.filter((b) => b.warehouse === 'CENTRAL' && b.type !== 'PREPARED');

  // Helper resolving packaging units and conversion rules for L2 transfer
  const resolveTransferItemDetails = (
    name: string,
    requestedUnit?: string,
    existingCentralQty?: number
  ) => {
    const cleanName = (name || '').trim().toLowerCase();
    const canonKey = toComparisonKey(name);
    const match = centralItems.find((c) => toComparisonKey(c.ingredient_name) === canonKey || c.ingredient_name.toLowerCase() === cleanName);
    const ingMeta = ingredients.find((i) => toComparisonKey(i.name) === canonKey || i.name.toLowerCase() === cleanName);
    const centralStdQty = existingCentralQty !== undefined ? existingCentralQty : (match?.quantity || 0);
    const stdUnit = match?.unit || ingMeta?.unit || 'g';

    // Find configured conversions for this ingredient from 04_Quy_doi (e.g. both Thùng and Gói)
    const itemConversions = conversions.filter(
      (c) => toComparisonKey(c.ingredient_name) === canonKey || c.ingredient_name.trim().toLowerCase() === cleanName
    );

    // Determine unit:
    // 1. requestedUnit if provided and valid
    // 2. First package unit from 04_Quy_doi (first priority in L2 transfer: xuất theo bao bì)
    // 3. Base standard unit from 02_Nguyen_lieu (fallback: g/ml)
    let unit = (requestedUnit || '').trim();
    if (!unit) {
      if (itemConversions.length > 0) {
        unit = itemConversions[0].package_unit.trim();
      } else {
        unit = stdUnit;
      }
    }

    // Find rule for this unit
    const cleanUnit = (unit || '').trim().toLowerCase();
    const cleanStdUnit = (stdUnit || '').trim().toLowerCase();
    const rule = itemConversions.find((c) => c.package_unit.trim().toLowerCase() === cleanUnit);

    let ratio = 1;
    let is_converted = false;

    if (rule && rule.ratio > 0) {
      ratio = rule.ratio;
      is_converted = true;
    } else if (cleanUnit && cleanUnit === cleanStdUnit) {
      ratio = 1;
      is_converted = true;
    } else if (cleanUnit === 'kg' && cleanStdUnit === 'g') {
      ratio = 1000;
      is_converted = true;
    } else if ((cleanUnit === 'l' || cleanUnit === 'lit' || cleanUnit === 'lít') && cleanStdUnit === 'ml') {
      ratio = 1000;
      is_converted = true;
    } else {
      ratio = 0;
      is_converted = false;
    }

    const maxAvailable = is_converted ? (ratio > 1 ? Math.floor(centralStdQty / ratio) : centralStdQty) : 0;

    return {
      unit,
      central_standard_qty: centralStdQty,
      standard_unit: rule?.standard_unit || stdUnit,
      ratio,
      is_converted,
      max_available: maxAvailable,
    };
  };

  const handleOcr = async (base64Image: string) => {
    setIsScanning(true);
    tg.haptic.impact('medium');
    const toastId = toast.loading('AI đang đọc phiếu chuyển kho...');

    try {
      const res = await api.scanStockTransferOcr(base64Image);
      if (res.items && res.items.length > 0) {
        // Kho Tổng không có BTP -> Lọc bỏ món BTP nếu OCR nhận diện nhầm
        const validItems = res.items.filter((it) => {
          const matchMeta = ingredients.find(
            (ing) => ing.name.toLowerCase() === it.ingredient_name.toLowerCase()
          );
          return !matchMeta || matchMeta.type !== 'PREPARED';
        });

        if (validItems.length < res.items.length) {
          toast('Đã lọc bỏ các món Bán thành phẩm vì Kho Tổng chỉ điều chuyển Nguyên liệu thô.', { icon: 'ℹ️' });
        }

        setTransferItems(
          validItems.map((it) => {
            const details = resolveTransferItemDetails(it.ingredient_name, it.unit);
            return {
              ingredient_name: it.ingredient_name,
              quantity: it.quantity,
              unit: details.unit,
              central_standard_qty: details.central_standard_qty,
              standard_unit: details.standard_unit,
              ratio: details.ratio,
              is_converted: details.is_converted,
              max_available: details.max_available,
            };
          })
        );
        toast.success(`Đã nhận diện ${validItems.length} món chuyển kho`, { id: toastId });
      } else {
        toast.error('Không tìm thấy dữ liệu chuyển kho từ ảnh', { id: toastId });
      }
    } catch (err: any) {
      toast.error(`Lỗi OCR: ${err.message}`, { id: toastId });
    } finally {
      setIsScanning(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      handleOcr(base64);
    };
    reader.readAsDataURL(file);
  };

  const setItemQty = (index: number, val: number) => {
    setTransferItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, quantity: Math.max(0, val) } : item))
    );
  };

  const setItemIngredient = (index: number, name: string, preferredUnit?: string, option?: any) => {
    const targetUnit = option?.package_unit || (preferredUnit && preferredUnit !== 'g' && preferredUnit !== 'ml' ? preferredUnit : undefined);
    const details = resolveTransferItemDetails(name, targetUnit);
    setTransferItems((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              ingredient_name: name,
              quantity: item.quantity > 0 ? item.quantity : 1,
              unit: details.unit,
              central_standard_qty: details.central_standard_qty,
              standard_unit: details.standard_unit,
              ratio: details.ratio,
              is_converted: details.is_converted,
              max_available: details.max_available,
            }
          : item
      )
    );
  };

  const removeRow = (index: number) => {
    tg.haptic.impact('light');
    setTransferItems((prev) => prev.filter((_, i) => i !== index));
  };

  const addRow = () => {
    tg.haptic.impact('light');
    setIsAddModalOpen(true);
  };

  const handleAddItem = (newItem: { ingredient_name: string; quantity: number; unit: string }) => {
    const details = resolveTransferItemDetails(newItem.ingredient_name, newItem.unit);
    setTransferItems((prev) => [
      ...prev,
      {
        ingredient_name: newItem.ingredient_name,
        quantity: newItem.quantity,
        unit: newItem.unit,
        central_standard_qty: details.central_standard_qty,
        standard_unit: details.standard_unit,
        ratio: details.ratio,
        is_converted: details.is_converted,
        max_available: details.max_available,
      },
    ]);
    toast.success(`Đã thêm "${newItem.ingredient_name}" (${newItem.quantity} ${newItem.unit}) vào danh sách chuyển`);
  };

  // Submit action hook
  const { isSubmitting, isDisabled, execute: submitTransfer } = useSubmitAction(
    async () => {
      const valid = transferItems.filter((t) => t.ingredient_name.trim() && t.quantity > 0);
      await api.transferStock(
        valid.map((v) => ({
          ingredient_name: v.ingredient_name,
          quantity: v.quantity,
          unit: v.unit,
        })),
        note
      );
    },
    {
      successMessage: 'Chuyển kho thành công! Đã trừ Kho Tổng và cộng Cửa Hàng.',
      onSuccess: () => {
        setStep('SUCCESS');
      },
    }
  );

  const goToConfirm = () => {
    const valid = transferItems.filter((t) => t.ingredient_name.trim() && t.quantity > 0);
    if (valid.length === 0) {
      toast.error('Vui lòng chọn ít nhất một nguyên liệu để chuyển.');
      return;
    }

    const unmapped = valid.find((t) => t.is_converted === false);
    if (unmapped) {
      toast.error(
        `Mặt hàng "${unmapped.ingredient_name}" (đơn vị: ${unmapped.unit}) chưa có quy tắc quy đổi sang "${unmapped.standard_unit}". Vui lòng khai báo trong tab 04_Quy_doi!`
      );
      return;
    }

    const overstock = valid.find((t) => t.quantity > t.max_available);
    if (overstock) {
      const extraHint = overstock.ratio > 1 ? ` (~${formatQty(overstock.central_standard_qty, overstock.standard_unit)})` : '';
      toast.error(
        `Không thể chuyển ${formatQty(overstock.quantity, overstock.unit)} vượt quá tồn kho tổng (${formatQty(overstock.max_available, overstock.unit)}${extraHint}).`
      );
      return;
    }

    tg.haptic.impact('light');
    setStep('CONFIRM');
  };

  // 1. Success Step
  if (step === 'SUCCESS') {
    return (
      <SuccessView
        title="Chuyển Kho Thành Công!"
        message={`Đã xuất ${transferItems.filter((t) => t.ingredient_name.trim() && t.quantity > 0).length} mặt hàng từ Kho Tổng sang Quầy Bar / Cửa Hàng.`}
        onDone={onSuccess}
      />
    );
  }

  // 2. Confirm Step
  if (step === 'CONFIRM') {
    const confirmItems: ConfirmItem[] = transferItems
      .filter((t) => t.ingredient_name.trim() && t.quantity > 0)
      .map((t) => {
        const stdQty = t.quantity * (t.ratio || 1);
        const stdText = t.ratio > 1 ? ` (~${formatQty(stdQty, t.standard_unit)})` : '';
        const maxText = t.ratio > 1 ? ` (~${formatQty(t.central_standard_qty, t.standard_unit)})` : '';
        return {
          label: t.ingredient_name,
          value: `${formatQty(t.quantity, t.unit)}${stdText}`,
          sub: `Tồn kho tổng hiện có: ${formatQty(t.max_available, t.unit)}${maxText}`,
          badge: 'Kho Tổng ➔ Cửa Hàng',
        };
      });

    return (
      <ConfirmView
        title="Xác Nhận Chuyển Kho Đầu Ca"
        description="Kiểm tra kỹ danh sách nguyên liệu sẽ xuất từ Kho Tổng vào Cửa Hàng:"
        items={confirmItems}
        note={note}
        onConfirm={submitTransfer}
        onBack={() => setStep('INPUT')}
        isSubmitting={isSubmitting}
        isDisabled={isDisabled}
        confirmText="Xác Nhận Xuất Chuyển Kho"
      />
    );
  }

  // 3. Input Step
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={onBack} className="stepper-btn" title="Quay lại">
          <ArrowLeft size={18} />
        </button>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 16, fontWeight: 800 }}>Luồng 2: Chuyển Kho Đầu Ca</h2>
          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Kho Tổng ➔ Cửa Hàng</span>
        </div>
        <div style={{ width: 36 }} />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 36, color: 'var(--text-tertiary)' }}>
          <RefreshCw size={24} className="animate-spin" style={{ margin: '0 auto 8px' }} />
          Đang tải danh sách tồn kho...
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* OCR Scan Banner */}
          <div className="card" style={{ padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <ClipboardCheck size={18} color="var(--primary-accent)" />
              <h3 style={{ fontSize: 14, fontWeight: 700 }}>Quét Phiếu Chuyển Kho (OCR AI)</h3>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <button
                onClick={() => setIsCameraOpen(true)}
                disabled={isScanning}
                className="btn btn-primary"
                style={{ fontSize: 13, minHeight: 44 }}
              >
                <Camera size={16} /> Chụp Ảnh
              </button>

              <label
                className={`btn btn-secondary ${isScanning ? 'opacity-50' : ''}`}
                style={{ fontSize: 13, minHeight: 44, cursor: 'pointer', margin: 0 }}
              >
                <Upload size={16} /> Chọn File
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  disabled={isScanning}
                  onChange={handleFileUpload}
                />
              </label>
            </div>

            {isScanning && (
              <div style={{ textAlign: 'center', padding: '12px 0', color: 'var(--primary-accent)', fontSize: 13 }}>
                <RefreshCw size={18} className="animate-spin" style={{ margin: '0 auto 6px' }} />
                Đang trích xuất phiếu chuyển kho bằng Gemini...
              </div>
            )}
          </div>

          {/* Card List of Items to Transfer */}
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="card-header" style={{ marginBottom: 4 }}>
              <span className="card-title">Nguyên Liệu Xuất Cho Ca Làm</span>
              <button onClick={addRow} className="badge badge-success" style={{ border: 'none', cursor: 'pointer', padding: '5px 8px' }}>
                <Plus size={12} /> Thêm món
              </button>
            </div>

            {/* Table Header */}
            {transferItems.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 76px 28px', gap: 8, padding: '0 11px', fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)' }}>
                <div>NGUYÊN LIỆU (KHO TỔNG)</div>
                <div style={{ textAlign: 'center' }}>SL XUẤT</div>
                <div></div>
              </div>
            )}

            {transferItems.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '28px 16px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--bg-surface-subtle)',
                  border: '1.5px dashed var(--border-color)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <div style={{ fontSize: 28 }}>🚚</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                  Chưa có nguyên liệu nào cần xuất
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  Bấm nút bên dưới hoặc Chụp ảnh phiếu chuyển để thêm món
                </div>
                <button
                  type="button"
                  onClick={addRow}
                  className="btn btn-primary"
                  style={{ minHeight: 38, padding: '6px 16px', fontSize: 13, marginTop: 4 }}
                >
                  <Plus size={15} /> Thêm Món Vào Phiếu
                </button>
              </div>
            ) : (
              transferItems.map((item, index) => (
                <div
                  key={index}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) 76px 28px',
                    gap: 8,
                    alignItems: 'flex-start',
                    padding: '8px 10px',
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--bg-surface-subtle)',
                    border: item.ingredient_name && item.is_converted === false
                      ? '1.5px solid var(--color-danger)'
                      : '1px solid var(--border-color)',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <ItemDropdown
                      value={item.ingredient_name}
                      selectedUnit={item.unit}
                      displayMode="name_with_unit"
                      items={transferOptions}
                      placeholder="Chọn nguyên liệu xuất..."
                      onChange={(selectedName, packageUnit, option) => setItemIngredient(index, selectedName, packageUnit, option)}
                    />
                    {item.ingredient_name && (
                      <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 4, paddingLeft: 2, lineHeight: 1.3 }}>
                        {item.is_converted === false ? (
                          <span style={{ color: 'var(--color-danger)', fontWeight: 700 }}>
                            ❌ Chưa có quy đổi "{item.unit}" ➔ "{item.standard_unit}" (Tab 04_Quy_doi)
                          </span>
                        ) : (
                          <>
                            Kho tổng: <span className="font-bold" style={{ color: 'var(--primary-accent)' }}>
                              {formatQty(item.max_available, item.unit)}
                            </span>
                            {item.ratio > 1 && (
                              <span style={{ color: 'var(--text-tertiary)' }}>
                                {` (~${formatQty(item.central_standard_qty, item.standard_unit)})`}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{ minWidth: 0, textAlign: 'center' }}>
                    <input
                      type="number"
                      className="input num"
                      placeholder="SL"
                      value={item.quantity === 0 ? '' : item.quantity}
                      onChange={(e) => setItemQty(index, Number(e.target.value))}
                      onBlur={(e) => {
                        if (!e.target.value) setItemQty(index, 0);
                      }}
                      style={{
                        textAlign: 'center',
                        fontWeight: 800,
                        fontSize: 15,
                        padding: '4px 2px',
                        minHeight: 38,
                        height: 38,
                        width: '100%',
                        boxSizing: 'border-box',
                      }}
                    />
                    {item.unit && (
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginTop: 4, textTransform: 'lowercase' }}>
                        {item.unit}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => removeRow(index)}
                    className="stepper-btn"
                    style={{
                      width: 28,
                      minHeight: 38,
                      height: 38,
                      color: 'var(--color-danger)',
                      justifySelf: 'center',
                      padding: 0,
                    }}
                    title="Xóa dòng"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))
            )}

            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>Ghi chú điều chuyển:</label>
              <input
                type="text"
                className="input"
                placeholder="Ví dụ: Xuất bổ sung giữa ca..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                style={{ marginTop: 4 }}
              />
            </div>
          </div>

          {/* Confirm Button */}
          <button
            onClick={goToConfirm}
            className="btn btn-primary btn-full"
            style={{ fontSize: 16, padding: '14px 20px', minHeight: 52 }}
          >
            <ArrowRight size={18} /> Xem & Xác Nhận Chuyển {transferItems.filter((t) => t.ingredient_name).length} Món
          </button>
        </div>
      )}

      {/* Camera Capture Modal */}
      <CameraCapture
        isOpen={isCameraOpen}
        onCapture={(base64) => handleOcr(base64.split(',')[1] || base64)}
        onClose={() => setIsCameraOpen(false)}
      />

      {/* Add Item Modal */}
      <AddTransferItemModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        ingredients={transferOptions}
        centralItems={centralItems}
        units={units}
        conversions={conversions}
        getInwardUnitsForItem={getInwardUnitsForItem}
        onAdd={handleAddItem}
      />
    </div>
  );
};
